import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import useNotificationStore from '../../store/notificationStore'
import { useNotificationTenantId } from '../../hooks/useNotificationTenantId'
import { useWsEvent } from '../../lib/realtime/wsSessionHooks'
import { WS_RESYNC_EVENT_TYPE } from '../../lib/realtime/wsClient'
import { onForegroundPush, clearAppBadge } from '../../lib/pushNotifications'

const ATTENTION_REFRESH_DEBOUNCE_MS = 1200

// Keeps the shared notification store in step with the server for the
// active branch. Mounted once beside WsSessionProvider (LangLayout), not in
// AppShell/DesktopShell — those remount on every route change, which used to
// refetch the bell on each navigation and lose its state in between.
// growchat-doc/manager-notification-page/spec-manager-notification-page.md
//
// What triggers a fetch, and how much it replaces:
//   - the active branch becomes ready or changes: load from scratch;
//   - the realtime session (re)opens: events may have been missed, so the
//     lists are replaced (WS_RESYNC_EVENT_TYPE, see wsClient.js);
//   - the tab regains attention, or a push arrives in the foreground: the
//     latest page is folded in without cutting the list.
// Everything else arrives as realtime events and is merged by the store.
export default function NotificationSync() {
  const notificationTenantId = useNotificationTenantId()
  const lastAttentionRefreshAtRef = useRef(0)

  useEffect(() => {
    useNotificationStore.getState().setTenant(notificationTenantId)
  }, [notificationTenantId])

  const applyEvent = (event) => useNotificationStore.getState().applyEvent(event)
  useWsEvent('notification.new', applyEvent)
  // Read state is shared by the whole branch: these arrive whenever anyone,
  // this screen included, marks read or deletes.
  useWsEvent('notification.read', applyEvent)
  useWsEvent('notification.read_all', applyEvent)
  useWsEvent('notification.deleted', applyEvent)

  useWsEvent(WS_RESYNC_EVENT_TYPE, () => {
    useNotificationStore.getState().resync()
  })

  useEffect(() => {
    clearAppBadge()
    function tryAttentionRefresh(source) {
      const now = Date.now()
      if (now - lastAttentionRefreshAtRef.current < ATTENTION_REFRESH_DEBOUNCE_MS) return
      lastAttentionRefreshAtRef.current = now
      useNotificationStore.getState().refresh(source)
      clearAppBadge()
    }
    function handleVisibilityRefresh() {
      if (document.visibilityState === 'visible') tryAttentionRefresh('visibility')
    }
    function handleWindowFocusRefresh() {
      tryAttentionRefresh('focus')
    }
    document.addEventListener('visibilitychange', handleVisibilityRefresh)
    window.addEventListener('focus', handleWindowFocusRefresh)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityRefresh)
      window.removeEventListener('focus', handleWindowFocusRefresh)
    }
  }, [])

  useEffect(() => {
    let unsubscribe = () => {}
    let cancelled = false
    onForegroundPush((payload) => {
      const { title, body } = payload.data || {}
      toast.info(`${title || ''}${body ? ` — ${body}` : ''}`.trim())
      useNotificationStore.getState().refresh('push')
    }).then((unsub) => {
      if (cancelled) unsub()
      else unsubscribe = unsub
    })
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  return null
}
